import { Box, Typography } from '@mui/material';
import React from 'react';
import rpcShared from '@rockpapercoin/rpc-shared';
import styles from './InvoiceViewTotals.module.scss';
import { gql, DocumentNode } from '@apollo/client';
import { InvoiceViewTotalsInvoiceFragment } from '#types';

const invoiceViewInvoiceTotalsPaymentInstallmentFragment = gql`
	fragment invoiceViewInvoiceTotalsPaymentInstallment on PaymentInstallment {
		status
		amountDue
		amountPaid
		convenienceFee
		amountRefunded
		paymentDate
	}
`;

const invoiceViewTotalsInvoiceFragment = gql`
	fragment invoiceViewTotalsInvoice on Invoice {
		id
		contact {
			id
			customer {
				id
				clientUser {
					id
					assignedPlanner {
						id
						organization {
							id
							users {
								id
								user {
									id
								}
							}
						}
					}
				}
			}
		}
		organization {
			id
			users {
				id
				user {
					id
				}
			}
		}
		lineItems {
			id
			quantity
			rate
			taxPercentage
		}
		paymentInstallments {
			...invoiceViewInvoiceTotalsPaymentInstallment
		}
	}
	${ invoiceViewInvoiceTotalsPaymentInstallmentFragment }
`;

export type InvoiceViewTotalsProps = {
	invoice?: InvoiceViewTotalsInvoiceFragment;
	requestingUserBaseId?: string;
};

type InvoiceViewTotalsPropsWithFragment = React.FC<InvoiceViewTotalsProps> & {
	fragments: {
		invoiceViewTotalsInvoiceFragment: DocumentNode;
	};
};

const DisplayValue: React.FC<{
	label: string;
	value: string;
	isBold?: true;
	isVisible?: boolean;
}> = ( { label, value, isBold, isVisible = true } ) =>
	isVisible ? (
		<Box className={ [ styles.row, isBold ? styles.bold : undefined ].join( ' ' ) }>
			<Typography>{ label }</Typography>
			<Typography data-cy={ label.replaceAll( ' ', '-' ).toLowerCase() }>
				{ value }
			</Typography>
		</Box>
	) : null;

export const InvoiceViewTotals: InvoiceViewTotalsPropsWithFragment = ( {
	invoice,
	requestingUserBaseId,
} ) => {
	const invoiceTotalsAsStrings = invoice
		? rpcShared.invoices.calculatePaymentTotalsToString( invoice )
		: undefined;
	const viewingUserIsVendor = !!invoice?.organization?.users?.find(
		( user ) => user.user?.id === requestingUserBaseId
	);
	const viewingUserIsCustomer = requestingUserBaseId
		? rpcShared.invoices.isCustomer( invoice, requestingUserBaseId )
		: false;
	const orgUser =
		invoice?.contact?.customer?.clientUser?.assignedPlanner?.organization?.users?.find(
			( user ) => user.user?.id === requestingUserBaseId
		);
	const viewingUserIsAPPA =
		rpcShared.invoices.isAssignedPlannerOrAdminOfCustomersPlannerOrg(
			invoice,
			orgUser
		);
	const viewingUserIsCustomerOrAPPA =
		viewingUserIsCustomer || viewingUserIsAPPA;
	if ( !invoiceTotalsAsStrings ) {
		return null;
	}
	return (
		<Box className={ styles.container }>
			<DisplayValue
				label='Gratuity Total'
				value={ invoiceTotalsAsStrings.gratuityTotal }
				isVisible={ invoiceTotalsAsStrings.gratuityTotal !== '$0.00' }
			/>
			<DisplayValue
				label='Convenience Charge Total'
				value={ invoiceTotalsAsStrings.convenienceChargeTotal }
				isVisible={
					viewingUserIsCustomerOrAPPA &&
					!viewingUserIsVendor &&
					invoiceTotalsAsStrings.convenienceChargeTotal !== '$0.00'
				}
			/>
			<DisplayValue
				label='Total Amount Paid'
				value={
					viewingUserIsVendor
						? invoiceTotalsAsStrings.totalAmountPaidLessCC
						: invoiceTotalsAsStrings.totalPaid
				}
			/>
			<DisplayValue
				label='Unpaid Balance'
				value={ invoiceTotalsAsStrings.unpaidBalance }
			/>
			<DisplayValue
				label='Total Refunded'
				value={ invoiceTotalsAsStrings.totalRefunded }
				isVisible={ invoiceTotalsAsStrings.totalRefunded !== '$0.00' }
			/>
		</Box>
	);
};

InvoiceViewTotals.fragments = {
	invoiceViewTotalsInvoiceFragment,
};
